/**
 * ReportOpenCard — the inline card a report arrives as, outside the guided flow.
 *
 * Why a card and not the report itself: a report is a DELIVERABLE. The split
 * every assistant with both surfaces settles on is that something you copy,
 * export or keep iterating on belongs in a panel, while something you look at to
 * answer the question in front of you belongs in the message. A report is the
 * former — but a panel that opens ITSELF takes the screen for something the user
 * has not decided to read yet, and outside the flow they usually have not.
 *
 * So: the card states what was produced, and the panel opens on the click.
 * Inside the guided flow the report still auto-opens (see chatStore's pin gate) —
 * there the canvas is already on screen and the user walked the funnel to reach
 * this stage, so asking for one more click would be ceremony.
 */
import { pinArtifact, ionSurface, expandToFullpage } from '../../state/chatStore.js'
import { IconLayers } from '../shared/Icons.jsx'

/**
 * @param {object} props
 * @param {object} props.artifact — the raw `report` artifact payload
 * @param {string} props.messageId
 * @param {string} [props.toolCallId]
 */
export function ReportOpenCard({ artifact, messageId, toolCallId }) {
  const title = artifact?.title || 'Report'
  // A count of sections/cells if the payload offers one — enough for the user to
  // judge whether it is worth opening, without rendering the thing.
  const sections = Array.isArray(artifact?.sections) ? artifact.sections.length : null

  function handleOpen() {
    pinArtifact({
      id: `report-${messageId}`,
      type: 'report',
      data: artifact,
      messageId,
      sourceToolCallId: toolCallId,
    })
    // The docked panel has no canvas — the report needs the full-page geometry to
    // have anywhere to render. Only expand when we are not already there, so the
    // click never collapses a user who IS on /assistant.
    if (ionSurface.peek() !== 'full') expandToFullpage()
  }

  return (
    <div class="ion-report-card">
      <IconLayers size={18} className="ion-report-card__icon" />
      <div class="ion-report-card__text">
        <span class="ion-report-card__title">{title}</span>
        {sections != null && (
          <span class="ion-report-card__meta">
            {sections} section{sections === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <button type="button" class="ion-report-card__open" onClick={handleOpen}>
        Open report
      </button>
    </div>
  )
}
